import { useCallback, useEffect, useMemo, useState } from 'react';
import { getSession } from '~/lib/auth';

/**
 * Calendar — month grid combining three layers, all in Alaska time:
 *
 *   · Phone-consult bookings (our D1 rows) — teal chips
 *   · Google Calendar events (when connected) — sage chips
 *   · Blackout dates — struck red days, with reason
 *
 * Days outside booking hours are shaded. Click a day → details panel
 * with everything on it + Block/Unblock. "Block a range" handles
 * vacations in one action; blocked days return zero bookable slots
 * immediately (the booking endpoint re-validates server-side).
 */

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.valleyvitalitywellness.com';
const TZ = 'America/Anchorage';

interface Booking {
  id: string;
  name: string;
  phone: string | null;
  email: string;
  slot_start: string;
  slot_end: string;
  status: string;
  consult_type: string;
}

interface CalEvent {
  id: string;
  summary: string | null;
  location: string | null;
  description: string | null;
  start: string;
  end: string;
  htmlLink?: string;
}

interface BlockedDate {
  date: string;
  reason: string | null;
}

interface DayCell {
  dateStr: string; // YYYY-MM-DD
  dayNum: number;
  inMonth: boolean;
  isToday: boolean;
  weekday: number; // 0=Sun
}

function authHeaders(): Record<string, string> {
  const session = getSession();
  return session
    ? { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

/** Weekday (0=Sun) of a pure calendar date — timezone-free math. */
function weekdayOf(y: number, m: number, d: number): number {
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** The YYYY-MM-DD a UTC instant falls on in Alaska. */
function akDateStr(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(iso));
}

function akTime(iso: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function CalendarPage() {
  const today = akDateStr(new Date().toISOString());
  const [year, setYear] = useState(() => Number(today.slice(0, 4)));
  const [month, setMonth] = useState(() => Number(today.slice(5, 7))); // 1-12
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [events, setEvents] = useState<CalEvent[]>([]);
  const [blocked, setBlocked] = useState<BlockedDate[]>([]);
  const [hours, setHours] = useState<Record<number, unknown>>({});
  const [gcalNote, setGcalNote] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [rangeReason, setRangeReason] = useState('');
  const [rangeSaving, setRangeSaving] = useState(false);

  const monthStr = `${year}-${pad(month)}`;
  const firstStr = `${monthStr}-01`;
  const lastStr = `${monthStr}-${pad(daysInMonth(year, month))}`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Pad the Google range a day each side so AK-vs-UTC boundary events land.
      const timeMin = new Date(Date.parse(`${firstStr}T00:00:00Z`) - 86400_000).toISOString();
      const timeMax = new Date(Date.parse(`${lastStr}T00:00:00Z`) + 2 * 86400_000).toISOString();
      const [bRes, eRes, blRes, sRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/bookings`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/admin/calendar?timeMin=${encodeURIComponent(timeMin)}&timeMax=${encodeURIComponent(timeMax)}`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/admin/blocked-dates?from=${firstStr}&to=${lastStr}`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/admin/settings`, { headers: authHeaders() }),
      ]);
      if (bRes.ok) {
        const data = (await bRes.json()) as { bookings: Booking[] };
        setBookings(data.bookings ?? []);
      }
      if (eRes.ok) {
        const data = (await eRes.json()) as { events?: CalEvent[]; error?: string };
        setEvents(data.events ?? []);
        setGcalNote(data.error ? 'Google Calendar not connected — connect it in Settings → Business to see events here.' : null);
      }
      if (blRes.ok) {
        const data = (await blRes.json()) as { blocked: BlockedDate[] };
        setBlocked(data.blocked ?? []);
      }
      if (sRes.ok) {
        const data = (await sRes.json()) as { settings: Record<string, string> };
        try {
          setHours(JSON.parse(data.settings.booking_hours ?? '{}'));
        } catch {
          setHours({});
        }
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [firstStr, lastStr]);

  useEffect(() => {
    void load();
  }, [load]);

  /* ─── Derived maps ─────────────────────────────────────────── */

  const bookingsByDay = useMemo(() => {
    const m = new Map<string, Booking[]>();
    for (const b of bookings) {
      if (b.status === 'cancelled') continue;
      const k = akDateStr(b.slot_start);
      (m.get(k) ?? m.set(k, []).get(k)!).push(b);
    }
    for (const list of m.values()) list.sort((a, b) => a.slot_start.localeCompare(b.slot_start));
    return m;
  }, [bookings]);

  const eventsByDay = useMemo(() => {
    const m = new Map<string, CalEvent[]>();
    for (const e of events) {
      const k = e.start.length === 10 ? e.start : akDateStr(e.start);
      (m.get(k) ?? m.set(k, []).get(k)!).push(e);
    }
    for (const list of m.values()) list.sort((a, b) => a.start.localeCompare(b.start));
    return m;
  }, [events]);

  const blockedMap = useMemo(() => new Map(blocked.map((b) => [b.date, b.reason])), [blocked]);

  const cells = useMemo<DayCell[]>(() => {
    const out: DayCell[] = [];
    const firstDow = weekdayOf(year, month, 1);
    const dim = daysInMonth(year, month);
    // Leading cells from the previous month.
    const prevY = month === 1 ? year - 1 : year;
    const prevM = month === 1 ? 12 : month - 1;
    const prevDim = daysInMonth(prevY, prevM);
    for (let i = firstDow - 1; i >= 0; i--) {
      const d = prevDim - i;
      out.push({ dateStr: `${prevY}-${pad(prevM)}-${pad(d)}`, dayNum: d, inMonth: false, isToday: false, weekday: weekdayOf(prevY, prevM, d) });
    }
    for (let d = 1; d <= dim; d++) {
      const ds = `${monthStr}-${pad(d)}`;
      out.push({ dateStr: ds, dayNum: d, inMonth: true, isToday: ds === today, weekday: weekdayOf(year, month, d) });
    }
    // Trailing cells to complete the final week.
    const nextY = month === 12 ? year + 1 : year;
    const nextM = month === 12 ? 1 : month + 1;
    let d = 1;
    while (out.length % 7 !== 0) {
      out.push({ dateStr: `${nextY}-${pad(nextM)}-${pad(d)}`, dayNum: d, inMonth: false, isToday: false, weekday: weekdayOf(nextY, nextM, d) });
      d++;
    }
    return out;
  }, [year, month, monthStr, today]);

  /* ─── Actions ──────────────────────────────────────────────── */

  function nav(delta: number) {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setMonth(m);
    setYear(y);
    setSelectedDay(null);
  }

  async function blockDates(dates: string[], reason: string) {
    const res = await fetch(`${API_BASE}/api/admin/blocked-dates`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ dates, reason: reason || undefined }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error || `Block failed (${res.status})`);
    }
  }

  async function toggleBlock(dateStr: string) {
    setError(null);
    try {
      if (blockedMap.has(dateStr)) {
        await fetch(`${API_BASE}/api/admin/blocked-dates/${dateStr}`, {
          method: 'DELETE',
          headers: authHeaders(),
        });
        setBlocked((prev) => prev.filter((b) => b.date !== dateStr));
      } else {
        await blockDates([dateStr], blockReason);
        setBlocked((prev) => [...prev, { date: dateStr, reason: blockReason || null }].sort((a, b) => a.date.localeCompare(b.date)));
        setBlockReason('');
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function blockRange() {
    if (!rangeStart || !rangeEnd || rangeEnd < rangeStart) {
      setError('Pick a valid start and end date.');
      return;
    }
    const dates: string[] = [];
    const start = Date.parse(`${rangeStart}T12:00:00Z`);
    const end = Date.parse(`${rangeEnd}T12:00:00Z`);
    for (let t = start; t <= end && dates.length <= 120; t += 86400_000) {
      dates.push(new Date(t).toISOString().slice(0, 10));
    }
    setRangeSaving(true);
    setError(null);
    try {
      await blockDates(dates, rangeReason);
      setRangeStart('');
      setRangeEnd('');
      setRangeReason('');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRangeSaving(false);
    }
  }

  const selected = selectedDay
    ? {
        dateStr: selectedDay,
        bookings: bookingsByDay.get(selectedDay) ?? [],
        events: eventsByDay.get(selectedDay) ?? [],
        blockedReason: blockedMap.get(selectedDay),
        isBlocked: blockedMap.has(selectedDay),
      }
    : null;

  const fmtSelected = selectedDay
    ? new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric' }).format(new Date(`${selectedDay}T12:00:00Z`))
    : '';

  /* ─── Render ───────────────────────────────────────────────── */

  return (
    <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 320px' : '1fr', gap: 18, alignItems: 'start', maxWidth: 1240 }}>
      <div>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ fontSize: 28, color: 'var(--primary)', margin: 0 }}>Calendar</h1>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <button type="button" onClick={() => nav(-1)} aria-label="Previous month" style={navBtn}>‹</button>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--primary)', minWidth: 150, textAlign: 'center' }}>
                {MONTH_NAMES[month - 1]} {year}
              </span>
              <button type="button" onClick={() => nav(1)} aria-label="Next month" style={navBtn}>›</button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
            <span style={{ color: 'var(--text-muted)' }}>Block a range:</span>
            <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} style={dateInput} />
            <span style={{ color: 'var(--text-muted)' }}>to</span>
            <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} style={dateInput} />
            <input type="text" value={rangeReason} onChange={(e) => setRangeReason(e.target.value)} placeholder="Reason (optional)" style={{ ...dateInput, width: 140 }} />
            <button
              type="button"
              onClick={() => void blockRange()}
              disabled={rangeSaving || !rangeStart || !rangeEnd}
              style={{
                background: 'var(--primary)', color: 'var(--card)', border: 'none',
                padding: '7px 12px', borderRadius: 7, fontSize: 12.5, fontWeight: 600,
                cursor: rangeSaving ? 'wait' : 'pointer',
              }}
            >
              {rangeSaving ? 'Blocking…' : 'Block'}
            </button>
          </div>
        </header>

        {gcalNote && (
          <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 10, background: 'rgba(244, 201, 93, 0.12)', border: '1px solid rgba(244, 201, 93, 0.4)', color: '#8a661a', fontSize: 12.5 }}>
            {gcalNote}
          </div>
        )}
        {error && (
          <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 10, background: 'rgba(220, 80, 80, 0.08)', border: '1px solid rgba(220, 80, 80, 0.3)', color: '#c44545', fontSize: 12.5 }}>
            {error}
          </div>
        )}

        <div style={{ background: 'var(--card)', border: '1px solid var(--line-soft)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--line-soft)' }}>
            {DOW.map((d) => (
              <div key={d} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', textAlign: 'center' }}>
                {d}
              </div>
            ))}
          </div>
          {loading ? (
            <p style={{ color: 'var(--text-muted)', padding: 24 }}>Loading…</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {cells.map((cell) => {
                const dayBookings = bookingsByDay.get(cell.dateStr) ?? [];
                const dayEvents = eventsByDay.get(cell.dateStr) ?? [];
                const isBlocked = blockedMap.has(cell.dateStr);
                const isOpen = !!(hours as Record<string, unknown>)[String(cell.weekday)];
                const isSelected = selectedDay === cell.dateStr;
                return (
                  <button
                    key={cell.dateStr}
                    type="button"
                    onClick={() => setSelectedDay(cell.dateStr)}
                    style={{
                      minHeight: 96,
                      textAlign: 'left',
                      padding: '6px 7px',
                      border: 'none',
                      borderRight: '1px solid var(--line-soft)',
                      borderBottom: '1px solid var(--line-soft)',
                      cursor: 'pointer',
                      background: isSelected
                        ? 'rgba(95, 168, 162, 0.12)'
                        : isBlocked
                          ? 'rgba(220, 80, 80, 0.06)'
                          : !isOpen
                            ? 'var(--surface)'
                            : 'var(--card)',
                      opacity: cell.inMonth ? 1 : 0.4,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                      outline: isSelected ? '2px solid var(--teal)' : 'none',
                      outlineOffset: -2,
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{
                        fontSize: 12.5,
                        fontWeight: cell.isToday ? 700 : 500,
                        color: cell.isToday ? 'var(--card)' : isBlocked ? '#c44545' : 'var(--primary)',
                        background: cell.isToday ? 'var(--teal)' : 'transparent',
                        borderRadius: 999,
                        minWidth: 20,
                        height: 20,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 4px',
                        textDecoration: isBlocked ? 'line-through' : 'none',
                      }}>
                        {cell.dayNum}
                      </span>
                      {isBlocked && <span style={{ fontSize: 10, color: '#c44545', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>blocked</span>}
                    </span>
                    {dayBookings.slice(0, 2).map((b) => (
                      <span key={b.id} style={chip('var(--teal)', 'rgba(95,168,162,0.14)')}>
                        {akTime(b.slot_start)} {b.name.split(' ')[0]}
                      </span>
                    ))}
                    {dayEvents.slice(0, Math.max(0, 2 - dayBookings.length)).map((e) => (
                      <span key={e.id} style={chip('var(--primary-alt, #4d6b64)', 'rgba(120,140,130,0.14)')}>
                        {e.start.length > 10 ? `${akTime(e.start)} ` : ''}{(e.summary ?? '(busy)').slice(0, 16)}
                      </span>
                    ))}
                    {dayBookings.length + dayEvents.length > 2 && (
                      <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                        +{dayBookings.length + dayEvents.length - 2} more
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <p style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 8 }}>
          Shaded days are outside booking hours. Click any day to see details or block it. All times Alaska.
        </p>
      </div>

      {selected && (
        <aside style={{ background: 'var(--card)', border: '1px solid var(--line-soft)', borderRadius: 14, padding: 16, position: 'sticky', top: 72 }}>
          <header style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--primary)', margin: 0, flex: 1 }}>
              {fmtSelected}
            </h2>
            <button type="button" onClick={() => setSelectedDay(null)} aria-label="Close" style={{ background: 'transparent', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
          </header>

          {selected.isBlocked ? (
            <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(220, 80, 80, 0.07)', border: '1px solid rgba(220, 80, 80, 0.25)', marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#c44545' }}>Blocked — no bookings</div>
              {selected.blockedReason && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{selected.blockedReason}</div>}
              <button
                type="button"
                onClick={() => void toggleBlock(selected.dateStr)}
                style={{ marginTop: 8, background: 'transparent', border: '1px solid var(--line)', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--text)' }}
              >
                Unblock this day
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              <input
                type="text"
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Reason (optional)"
                style={{ ...dateInput, flex: 1 }}
              />
              <button
                type="button"
                onClick={() => void toggleBlock(selected.dateStr)}
                style={{ background: '#c44545', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
              >
                Block day
              </button>
            </div>
          )}

          <h3 style={sideHead}>Phone consults ({selected.bookings.length})</h3>
          {selected.bookings.length === 0 ? (
            <p style={sideEmpty}>None booked.</p>
          ) : (
            selected.bookings.map((b) => (
              <div key={b.id} style={sideCard('var(--teal)')}>
                <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--primary)' }}>
                  {akTime(b.slot_start)} — {b.name}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{b.phone ?? b.email} · {b.status}</div>
              </div>
            ))
          )}

          <h3 style={sideHead}>Google Calendar ({selected.events.length})</h3>
          {selected.events.length === 0 ? (
            <p style={sideEmpty}>Nothing on the calendar.</p>
          ) : (
            selected.events.map((e) => (
              <div key={e.id} style={sideCard('var(--line)')}>
                <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--primary)' }}>
                  {e.start.length > 10 ? `${akTime(e.start)} — ` : 'All day — '}{e.summary ?? '(busy)'}
                </div>
                {e.location && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{e.location}</div>}
                {e.htmlLink && (
                  <a href={e.htmlLink} target="_blank" rel="noreferrer" style={{ fontSize: 11.5, color: 'var(--teal)' }}>
                    Open in Google ↗
                  </a>
                )}
              </div>
            ))
          )}
        </aside>
      )}
    </div>
  );
}

const navBtn: React.CSSProperties = {
  background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 7,
  width: 28, height: 28, fontSize: 16, color: 'var(--primary)', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

const dateInput: React.CSSProperties = {
  fontFamily: 'inherit', fontSize: 12.5, padding: '6px 8px',
  border: '1px solid var(--line)', borderRadius: 7,
  background: 'var(--card)', color: 'var(--text)',
};

function chip(color: string, bg: string): React.CSSProperties {
  return {
    fontSize: 10.5, fontWeight: 600, color, background: bg,
    borderRadius: 5, padding: '2px 5px',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    maxWidth: '100%', display: 'block', textAlign: 'left',
  };
}

const sideHead: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
  color: 'var(--text-muted)', margin: '14px 0 6px',
};

const sideEmpty: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-muted)', margin: 0 };

function sideCard(accent: string): React.CSSProperties {
  return {
    padding: '8px 10px', borderRadius: 8, background: 'var(--surface)',
    borderLeft: `3px solid ${accent}`, marginBottom: 6,
  };
}
